import { GRADES } from '@/browse/grades'
import type { GoalHit } from '@/goals/goal_types'

interface SortOptions {
  isKeywordActive: boolean
  isSubjectSelected: boolean
}

interface GroupOptions {
  profileSubjects: string[]
  selectedSubject: string
}

export interface GroupedGoalHits {
  orderedSubjects: string[]
  resultsBySubject: Record<string, GoalHit[]>
}

// Legacy goals are retired and the retirement reindex removes them, but until it lands search
// still returns them, and their goal pages are gone. Delete this once the index has none.
export function withoutLegacyGoals(goals: GoalHit[]): GoalHit[] {
  return goals.filter((goal) => goal.super_page)
}

// GRADES lists the grades lowest first, so a grade's position is its rank.
function gradeRank(grade: string): number {
  return GRADES.indexOf(grade)
}

// Comparators return 0 for a tie (NaN, from a goal without a grade range, also counts as one),
// so each sorting step only decides between goals the steps before it left tied.
function higherFirst(valueA: number, valueB: number): number {
  return Math.sign(valueB - valueA)
}

function trueFirst(flagA: boolean, flagB: boolean): number {
  return Number(flagB) - Number(flagA)
}

function hasAccessPointLevel({ access_point_level }: GoalHit): boolean {
  return Boolean(access_point_level)
}

function topGrade({ grades }: GoalHit): number {
  return gradeRank(grades[grades.length - 1])
}

function compareGradesHighestFirst(goalA: GoalHit, goalB: GoalHit, isGradeRangeIndexed: boolean): number {
  if (!isGradeRangeIndexed) {
    return higherFirst(topGrade(goalA), topGrade(goalB))
  }
  const highestGradeOrder = higherFirst(goalA.highest_grade as number, goalB.highest_grade as number)
  if (highestGradeOrder) {
    return highestGradeOrder
  }
  return higherFirst(goalA.lowest_grade as number, goalB.lowest_grade as number)
}

function compareForFilterOnlySearch(goalA: GoalHit, goalB: GoalHit, isGradeRangeIndexed: boolean, isSubjectSelected: boolean): number {
  const accessPointOrder = trueFirst(hasAccessPointLevel(goalA), hasAccessPointLevel(goalB))
  if (accessPointOrder) {
    return accessPointOrder
  }
  const gradeOrder = compareGradesHighestFirst(goalA, goalB, isGradeRangeIndexed)
  if (gradeOrder) {
    return gradeOrder
  }
  if (!isSubjectSelected) {
    return 0
  }
  return higherFirst(goalA.score, goalB.score)
}

// Algolia ranks a keyword search by relevance, so only a filter-only search is reordered.
export function sortGoalHits(goals: GoalHit[], { isKeywordActive, isSubjectSelected }: SortOptions): GoalHit[] {
  if (isKeywordActive || !goals.length) {
    return goals
  }
  const isGradeRangeIndexed = Number.isInteger(goals[0].highest_grade)
  return [...goals].sort((goalA, goalB) => compareForFilterOnlySearch(goalA, goalB, isGradeRangeIndexed, isSubjectSelected))
}

function displaySubject(goal: GoalHit, profileSubjects: string[]): string {
  const isPrimarySubjectInProfile = profileSubjects.includes(goal.primary_subject)
  const isSubjectInProfile = profileSubjects.includes(goal.subject)
  if (!isPrimarySubjectInProfile && isSubjectInProfile) {
    return goal.subject
  }
  return goal.primary_subject
}

function withSelectedSubjectFirst(subjects: string[], selectedSubject: string): string[] {
  if (!subjects.includes(selectedSubject)) {
    return subjects
  }
  const otherSubjects = subjects.filter((subject) => subject !== selectedSubject)
  return [selectedSubject, ...otherSubjects]
}

// Subjects keep the order their first goal came back in, except the chosen subject, which leads.
export function groupGoalHitsBySubject(goals: GoalHit[], { profileSubjects, selectedSubject }: GroupOptions): GroupedGoalHits {
  const resultsBySubject: Record<string, GoalHit[]> = {}
  for (const goal of goals) {
    const subject = displaySubject(goal, profileSubjects)
    resultsBySubject[subject] = [...(resultsBySubject[subject] ?? []), goal]
  }
  const subjectsInResultOrder = Object.keys(resultsBySubject)
  return { orderedSubjects: withSelectedSubjectFirst(subjectsInResultOrder, selectedSubject), resultsBySubject }
}

export interface AccessSplit<T> {
  available: T[]
  restricted: T[]
}

export function splitByAccess<T>(goals: T[], isRestricted: (goal: T) => boolean): AccessSplit<T> {
  return {
    available: goals.filter((goal) => !isRestricted(goal)),
    restricted: goals.filter(isRestricted)
  }
}
